/**
 * "Save for offline": copies every file the built game needs into the cache the service worker (public/sw.js) serves
 * from when there is no network. The build writes offline-manifest.json (tools/offline-manifest.ts) listing each file
 * with its size and content hash, so later saves fetch only what changed and drop what a new build no longer uses.
 */
const CACHE = 'sky-harbor-offline';
const MANIFEST = 'offline-manifest.json';
const CONCURRENCY = 6;

export interface OfflineManifest { version: string; bytes: number; files: [path: string, size: number, hash: string][] }
export type OfflineState =
    | { kind: 'unsupported' }
    | { kind: 'missing'; bytes?: number }
    | { kind: 'saved'; current: boolean | null; bytes: number };

const supported = () => import.meta.env.PROD && 'serviceWorker' in navigator && typeof caches !== 'undefined' && isSecureContext;
const absolute = (path: string) => new URL(path, document.baseURI).href;

export function registerOfflineWorker(): void {
    if (!supported()) return;
    navigator.serviceWorker.register('./sw.js').catch(error => console.warn('Offline support is unavailable.', error));
}

async function saved(): Promise<OfflineManifest | null> {
    try {
        const response = await (await caches.open(CACHE)).match(absolute(MANIFEST));
        return response ? await response.json() as OfflineManifest : null;
    } catch { return null; }
}

async function latest(): Promise<OfflineManifest | null> {
    try {
        const response = await fetch(absolute(MANIFEST), { cache: 'no-store' });
        return response.ok ? await response.json() as OfflineManifest : null;
    } catch { return null; }
}

export async function offlineState(): Promise<OfflineState> {
    if (!supported()) return { kind: 'unsupported' };
    const [have, want] = await Promise.all([saved(), latest()]);
    if (!have) return { kind: 'missing', bytes: want?.bytes };
    return { kind: 'saved', current: want ? want.version === have.version : null, bytes: have.bytes };
}

/** Downloads the files that differ from the saved copy. `progress` receives a 0–1 fraction of the bytes to fetch. */
export async function saveOffline(progress: (fraction: number) => void): Promise<void> {
    if (!supported()) throw new Error('This browser cannot save the game for offline play.');
    await navigator.serviceWorker.ready;
    const want = await latest();
    if (!want) throw new Error('Connect to the internet to save the game for offline play.');
    const have = await saved();
    const cache = await caches.open(CACHE);
    const known = new Map((have?.files ?? []).map(([path, , hash]) => [path, hash]));
    const todo: OfflineManifest['files'] = [];
    for (const file of want.files)
        if (known.get(file[0]) !== file[2] || !(await cache.match(absolute(file[0])))) todo.push(file);
    // Ask the browser not to evict the pack when storage runs low; refusal still leaves a working (evictable) copy.
    await navigator.storage?.persist?.().catch(() => false);
    const total = todo.reduce((sum, [, size]) => sum + size, 0) || 1;
    let done = 0, next = 0;
    progress(0);
    const worker = async () => {
        while (next < todo.length) {
            const [path, size] = todo[next++];
            const response = await fetch(absolute(path), { cache: 'no-cache' });
            if (!response.ok) throw new Error(`Could not download ${path} (${response.status}).`);
            try { await cache.put(absolute(path), response); }
            catch (error) {
                throw new Error(error instanceof DOMException && error.name === 'QuotaExceededError'
                    ? 'This phone does not have enough free storage to save the game for offline play.' : `Could not save ${path}.`);
            }
            done += size;
            progress(done / total);
        }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    const keep = new Set(want.files.map(([path]) => absolute(path)));
    keep.add(absolute(MANIFEST));
    for (const request of await cache.keys())
        if (!keep.has(request.url)) await cache.delete(request);
    // The manifest goes in last: it marks the pack complete.
    await cache.put(absolute(MANIFEST), new Response(JSON.stringify(want), { headers: { 'Content-Type': 'application/json' } }));
    progress(1);
}

export const megabytes = (bytes: number) => `${Math.max(1, Math.round(bytes / 1e6))} MB`;
